/* Public registry consumer only. Generated assets remain the source of record content. */
(() => {
  'use strict';
  if (customElements.get('ss-proof-records')) return;
  const requests = new Map();
  function read(url) {
    if (!requests.has(url)) requests.set(url, fetch(url, { credentials: 'omit' }).then(r => {
      if (!r.ok) throw new Error('Data unavailable');
      return r.json();
    }).catch(e => { requests.delete(url); throw e; }));
    return requests.get(url);
  }
  // Disinfectant for a ZIP from ss-water-zip.json (EPA water system records; C/L = utility override).
  // Same rule and wording as the welcome popup: no single answer -> "chlorine or chloramine".
  const zipCode = (table, zip) => { const row = table?.z?.[String(zip || '').slice(0, 3)]; return row && /^\d{5}$/.test(zip) ? row.charAt(Number(zip.slice(3))) : ''; };
  const disinfectant = (table, zip) => {
    const c = zipCode(table, zip), agent = /c/i.test(c) ? 'chloramine' : /l/i.test(c) ? 'chlorine' : '';
    return agent ? `${agent} (${c === 'C' || c === 'L' ? 'water utility' : 'EPA'})` : 'chlorine or chloramine';
  };
  // Source of the disinfectant line: the utility's own report for the C/L overrides, else EPA records.
  const disinfectantSource = (table, zip) => /^[CL]$/.test(zipCode(table, zip)) ? "the water utility's own report" : 'US EPA water system records';
  const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]));
  const photo = entry => {
    const value = entry.shopify_image || '';
    return value.startsWith('shopify://shop_images/') ? '/cdn/shop/files/' + encodeURIComponent(value.slice(22)) : '';
  };
  // The welcome map shares the public registry and its real map coordinates.
  // All records contribute; nearby locations cluster at the rendered map scale.
  class MapPreview extends HTMLElement {
    connectedCallback() {
      if(this.observer)return;
      this.observer=new ResizeObserver(()=>this.render());this.observer.observe(this);
      read(this.dataset.registry).then(data=>{this.entries=(data.entries||[]).filter(e=>photo(e)&&Number.isFinite(e.coords?.x)&&Number.isFinite(e.coords?.y));if(this.isConnected)this.render();}).catch(()=>{});
    }
    render() {
      const width=this.clientWidth;if(!this.entries||!width)return;
      const size=matchMedia('(max-width:749px)').matches?24:34,distance=(size+8)/width*959,groups=[];
      this.entries.forEach(e=>{const g=groups.find(g=>Math.hypot(g.x-e.coords.x,g.y-e.coords.y)<distance);if(g){const n=g.entries.length;g.x=(g.x*n+e.coords.x)/(n+1);g.y=(g.y*n+e.coords.y)/(n+1);g.entries.push(e);}else groups.push({x:e.coords.x,y:e.coords.y,entries:[e]});});
      const inset=(size/2+6)/width*959;
      this.querySelector('.ss-popup-map__pins').innerHTML=groups.map(g=>`<span class="ss-popup-map__pin" data-count="${g.entries.length}" style="left:${Math.max(inset,Math.min(959-inset,g.x))/959*100}%;top:${Math.max(inset,Math.min(593-inset,g.y))/593*100}%"><img src="${photo(g.entries[0])}?width=100" width="34" height="34" alt="" loading="lazy">${g.entries.length>1?`<small>${g.entries.length}</small>`:''}</span>`).join('');
    }
    disconnectedCallback(){this.observer?.disconnect();this.observer=null;}
  }
  customElements.define('ss-map-preview',MapPreview);
  class ProofRecords extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.copy = JSON.parse(this.querySelector('[data-copy]').textContent);
      this.find = selector => this.querySelector(selector);
      this.icon = name => this.find(`[data-proof-icon="${name}"]`)?.innerHTML || '';
      this.grid = this.find('[data-grid]');
      this.dialog = this.find('dialog');
      this.body = this.find('[data-panel-body]');
      this.search = this.find('[data-search]');
      this.state = this.find('[data-state]');
      this.limit = Number(this.dataset.pageSize) || 12;
      this.entries = [];
      this.registryLoaded = false;
      this.listen = new AbortController();
      const opts = { signal: this.listen.signal };
      this.addEventListener('click', event => this.onClick(event), opts);
      this.body.addEventListener('touchstart', event => { if (!event.target.closest('.ss-proof-panel-photo')) return; this.photoTouch={x:event.touches[0].clientX,y:event.touches[0].clientY}; }, {...opts,passive:true});
      this.body.addEventListener('touchend', event => { if(!this.photoTouch)return;const x=event.changedTouches[0].clientX-this.photoTouch.x,y=event.changedTouches[0].clientY-this.photoTouch.y;this.photoTouch=null;if(Math.abs(x)>60&&Math.abs(x)>Math.abs(y)*1.5)this.body.querySelector(`[data-photo-step="${x<0?1:-1}"]`)?.click(); }, {...opts,passive:true});
      this.search.addEventListener('input', () => this.filter(), opts);
      this.state.addEventListener('change', () => this.filter(), opts);
      this.find('[data-zip-form]').addEventListener('submit', event => this.lookup(event), opts);
      this.resize = new ResizeObserver(() => { if (this.filtered && !this.find('.ss-proof-map-wrap').hidden) this.map(); });
      this.resize.observe(this.find('.ss-proof-map'));
      this.load();
    }
    disconnectedCallback() {
      this.listen?.abort();
      this.resize?.disconnect();
      if (this.dialog?.open) window.SSUI?.close(this.dialog, true);
      this.ready = false;
    }
    async load() {
      this.find('[data-retry]').hidden = true;
      try {
        const data = await read(this.dataset.registry);
        if (!Array.isArray(data.entries)) throw new Error('Invalid registry');
        if (!this.isConnected) return;
        const product = this.dataset.product || '';
        this.entries = data.entries.filter(e => e && photo(e) && (!product || e.product === product));
        this.registryLoaded = true;
        const states = [...new Set(this.entries.map(e => e.state).filter(Boolean))].sort();
        this.state.innerHTML = '<option value="">' + esc(this.copy.all_states) + '</option>' + states.map(s => `<option value="${esc(s)}">${esc(s)}</option>`).join('');
        this.find('[data-fallback]').hidden = true;
        this.grid.hidden = false;
        this.find('[data-tools]').hidden = false;
        this.find('[data-map-toggle]').hidden = false;
        this.filter();
      } catch (_) {
        this.find('[data-count]').textContent = this.copy.load_error;
        this.find('[data-fallback]').hidden = false;
        this.grid.hidden = true;
        this.find('[data-retry]').hidden = false;
      }
    }
    filter() {
      this.visibleCount = this.limit;
      const term = this.search.value.trim().toLocaleLowerCase();
      this.filtered = this.entries.filter(e => (!this.state.value || e.state === this.state.value) && (!term || `${e.location || ''} ${e.state || ''}`.toLocaleLowerCase().includes(term)));
      this.render();
      if (!this.find('.ss-proof-map-wrap').hidden) this.map();
    }
    render() {
      this.grid.innerHTML = this.filtered.slice(0, this.visibleCount).map(e => {
        const index = this.entries.indexOf(e);
        const duration = Number.isFinite(e.days) && e.days > 0 ? `${e.days} days` : this.copy.unknown_duration;
        return `<button type="button" class="ss-proof-card" data-record="${index}" aria-label="${esc(e.location || this.copy.source_label)}: ${esc(duration)}"><img class="ss-proof-card__image" src="${photo(e)}?width=600" srcset="${photo(e)}?width=400 400w, ${photo(e)}?width=800 800w" sizes="(min-width:990px) 23vw, (min-width:750px) 30vw, 45vw" width="600" height="450" loading="lazy" decoding="async" alt="${esc(e.location || '')} customer filter, ${esc(duration)}"><span class="ss-proof-card__info"><span><span class="ss-proof-card__location">${esc(e.location || this.copy.source_label)}</span><span class="ss-proof-card__meta">${esc(duration)}${e.name ? ' · ' + esc(e.name) : ''}</span></span><span class="ss-proof-card__open" aria-hidden="true">+</span></span></button>`;
      }).join('');
      this.find('[data-count]').textContent = `${this.filtered.length} ${this.copy.count_label}`;
      this.find('[data-empty]').hidden = this.filtered.length !== 0;
      this.find('[data-more]').hidden = this.visibleCount >= this.filtered.length;
    }
    onClick(event) {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-record')) this.openRecord(this.entries[Number(button.dataset.record)], button);
      if (button.hasAttribute('data-map-cluster')) {
        const group = button.dataset.mapCluster.split(',').map(Number).map(i => this.entries[i]).filter(Boolean);
        this.openGroup(group, button);
      }
      if (button.hasAttribute('data-photo-step')) {
        const index = (this.recordGroup.indexOf(this.current) + Number(button.dataset.photoStep) + this.recordGroup.length) % this.recordGroup.length;
        this.current = this.recordGroup[index];
        this.find('[data-panel-title]').textContent = this.current.location || this.copy.source_label;
        this.renderRecord(this.current);
        this.body.querySelector(`[data-photo-step="${button.dataset.photoStep}"]`)?.focus({preventScroll:true});
      }
      if (button.hasAttribute('data-reset')) { this.search.value = ''; this.state.value = ''; this.filter(); (this.classList.contains('ss-proof-records--map') ? this.find('[name=zip]') : this.search).focus(); }
      if (button.hasAttribute('data-more')) {
        const previous = this.visibleCount;
        this.visibleCount += this.limit; this.render();
        this.grid.children[previous]?.focus({ preventScroll: true });
      }
      if (button.hasAttribute('data-map-toggle')) {
        const map = this.find('.ss-proof-map-wrap');
        map.hidden = !map.hidden;
        button.setAttribute('aria-expanded', String(!map.hidden));
        if (!map.hidden) this.map();
      }
      if (button.hasAttribute('data-retry')) this.load();
      if (button.hasAttribute('data-original')) {
        window.SSUI.view(photo(this.current), `${this.current.location} original customer filter photograph`, this.current.location, this.recordOpener);
        const viewer = document.getElementById('ss-viewer');
        const back = document.createElement('button');
        back.type = 'button'; back.className = 'ss-btn ss-btn--quiet ss-proof-viewer-back';
        back.textContent = this.copy.back_label;
        viewer.querySelector('.ss-viewer__panel').append(back);
        viewer.addEventListener('ss:close', () => back.remove(), { once: true });
        back.addEventListener('click', () => {
          window.SSUI.close(viewer, true);
          window.SSUI.open(this.dialog, this.recordOpener);
          this.body.querySelector('[data-original]').focus();
        });
      }
      if (button.hasAttribute('data-browse-state')) {
        const state = button.dataset.browseState;
        if (![...this.state.options].some(o => o.value === state)) this.state.add(new Option(state, state));
        this.state.value = state; this.search.value = ''; this.filter();
        window.SSUI.close(this.dialog, true);
        this.scrollIntoView({ behavior: 'instant', block: 'start' });
        this.state.focus({ preventScroll: true });
      }
    }
    map() {
      const list = this.filtered.filter(e => Number.isFinite(e.coords?.x) && Number.isFinite(e.coords?.y));
      const width = this.find('.ss-proof-map').clientWidth || 959;
      const distance = 56 / width * 959;
      const groups = [];
      list.forEach(e => {
        const group = groups.find(g => Math.hypot(g.x-e.coords.x,g.y-e.coords.y) < distance);
        if (group) {const n=group.entries.length;group.x=(group.x*n+e.coords.x)/(n+1);group.y=(group.y*n+e.coords.y)/(n+1);group.entries.push(e);}
        else groups.push({x:e.coords.x,y:e.coords.y,entries:[e]});
      });
      this.find('[data-pins]').innerHTML = groups.map(g => {
        const e=g.entries[0], count=g.entries.length, inset=24/width*959;
        const x=Math.max(inset,Math.min(959-inset,g.x)),y=Math.max(inset,Math.min(593-inset,g.y));
        return `<button type="button" class="ss-proof-pin" style="left:${x/959*100}%;top:${y/593*100}%" ${count>1?`data-map-cluster="${g.entries.map(e=>this.entries.indexOf(e)).join(',')}"`:`data-record="${this.entries.indexOf(e)}"`} aria-label="${esc(e.location)}${count>1?', '+count+' nearby records':''}" title="${esc(e.location)}"><img src="${photo(e)}?width=100" width="40" height="40" loading="lazy" alt="">${count>1?`<span>${count}</span>`:''}</button>`;
      }).join('');
      const states = new Set(list.map(e => e.state));
      this.querySelectorAll('.ssfm__state').forEach(el => el.classList.toggle('is-present',states.has(el.dataset.state)));
    }
    async water() { try { return await read(this.dataset.water); } catch (_) { return null; } }
    async zipTable() { try { return await read(this.dataset.waterZip); } catch (_) { return null; } }
    regional(data, state, zip, table) {
      const w = data?.default_by_state?.[state];
      if (!w) return `<p class="ss-proof-caption">${esc(this.copy.water_missing)}</p>`;
      return `<section class="ss-proof-region"><h3>${this.icon('droplet')}<span>${esc(this.copy.regional_title)}${state ? ': '+esc(state) : ''}</span></h3><dl class="ss-proof-facts"><div><dt>${esc(this.copy.hardness_label)}</dt><dd>${esc(w.hardness || this.copy.unknown_duration)}</dd></div><div><dt>${esc(this.copy.treatment_label)}</dt><dd>${esc(disinfectant(table, zip))}${zip ? ` · ZIP ${esc(zip)}` : ''}</dd></div></dl><details class="ss-proof-method"><summary>${esc(this.copy.method_label)}</summary><p class="ss-proof-caption">${esc(this.copy.regional_note)}</p><p class="ss-proof-caption ss-proof-source">${esc(this.copy.source_prefix)}: ${esc(this.copy.water_source)}; ${esc(disinfectantSource(table, zip))} (disinfectant)</p></details><a class="ss-link" href="/pages/your-water">${esc(this.copy.water_link_label)} ${this.icon('arrow-right')}</a></section>`;
    }
    openGroup(group, opener, lookup = '', lookupZip = '') {
      if (!group.length) return;
      this.recordGroup = group;
      this.lookupContext = lookup;
      this.lookupZip = lookupZip;   // a visitor's ZIP lookup: the water facts are for their ZIP, not the record's
      this.current = group[0]; this.recordOpener = opener;
      this.find('[data-panel-title]').textContent = this.current.location || this.copy.source_label;
      this.renderRecord(this.current);
      window.SSUI.open(this.dialog, opener);
    }
    openRecord(entry, opener) {
      if (!entry) return;
      const group = this.entries.filter(e => e.state === entry.state);
      this.openGroup([entry, ...group.filter(e => e !== entry)], opener);
    }
    renderRecord(entry) {
      const request = this.panelRequest = (this.panelRequest || 0) + 1;
      const index = this.recordGroup.indexOf(entry), count = this.recordGroup.length;
      const navigation = count > 1 ? `<div class="ss-proof-photo-nav" role="group" aria-label="${esc(this.copy.nearby_label)}"><button class="ss-icon-button" type="button" data-photo-step="-1" aria-label="${esc(this.copy.previous_photo_label)}">${this.icon('arrow-left')}</button><span role="status" aria-live="polite">${index+1} / ${count}</span><button class="ss-icon-button" type="button" data-photo-step="1" aria-label="${esc(this.copy.next_photo_label)}">${this.icon('arrow-right')}</button></div>` : '';
      this.body.innerHTML = `${this.lookupContext ? `<p class="ss-proof-caption ss-proof-lookup-context">${esc(this.lookupContext)}</p>` : ''}<figure class="ss-proof-photo-frame"><img class="ss-proof-panel-photo" src="${photo(entry)}?width=1000" width="800" height="600" alt="${esc(entry.location)} original customer filter photograph"><figcaption><button type="button" class="ss-link" data-original>${this.icon('expand')}${esc(this.copy.photo_label)}</button>${navigation}</figcaption></figure><dl class="ss-proof-record-facts"><div class="ss-proof-duration"><dt>${this.icon('clock')}${esc(this.copy.duration_label)}</dt><dd>${esc(entry.days > 0 ? entry.days : this.copy.unknown_duration)}</dd></div>${entry.model ? `<div class="ss-proof-model"><dt>${esc(this.copy.model_label)}</dt><dd>${esc(entry.model)}</dd></div>` : ''}</dl><section class="ss-proof-account">${entry.quote && this.dataset.hideQuotes !== 'true' ? `<div class="ss-proof-quote"><p class="ss-proof-quote-label">${esc(this.copy.quote_label)}</p><blockquote>${esc(entry.quote)}<footer>${esc(entry.name)}</footer></blockquote></div>` : ''}<p class="ss-proof-caption ss-proof-record-source">${esc(this.copy.source_label)}${entry.name ? ': '+esc(entry.name) : ''}. ${esc(this.copy.record_note)}</p></section><div data-regional></div>`;
      Promise.all([this.water(), this.zipTable()]).then(([data, table]) => { if (this.panelRequest === request) { const region = this.body.querySelector('[data-regional]'); if (region) region.innerHTML = this.regional(data, entry.state, this.lookupZip || entry.zip, table); } });
    }
    async lookup(event) {
      event.preventDefault();
      const form = event.currentTarget, zip = form.elements.zip.value.trim(), button = form.querySelector('button');
      const status = this.find('[data-zip-status]');
      if (!/^\d{5}$/.test(zip)) { status.textContent = this.copy.zip_error; return; }
      button.disabled = true; form.setAttribute('aria-busy','true'); status.textContent = '';
      try {
        const [place, water, table] = await Promise.all([read('https://api.zippopotam.us/us/'+zip), this.water(), this.zipTable()]);
        const location = place.places?.[0], state = location?.['state abbreviation'];
        if (!state) throw new Error('ZIP unavailable');
        if (!this.isConnected) return;
        const group = this.entries.filter(e => e.state === state);
        const city = location['place name'];
        group.sort((a,b) => Number((b.location||'').toLowerCase().includes(city.toLowerCase())) - Number((a.location||'').toLowerCase().includes(city.toLowerCase())));
        this.state.value = [...this.state.options].some(o=>o.value===state) ? state : '';
        this.search.value = ''; this.filter();
        const context = `${city}, ${state} · ZIP ${zip}. ${this.copy.zip_scope_note}`;
        if (group.length) this.openGroup(group,button,context,zip);
        else {
          this.panelRequest = (this.panelRequest || 0) + 1;
          this.find('[data-panel-title]').textContent = `${city}, ${state}`;
          this.body.innerHTML = `<p class="ss-proof-caption">${esc(this.copy.lookup_label)} · ${esc(zip)}</p><p>${esc(this.registryLoaded ? this.copy.no_state_records : this.copy.load_error)}</p>` + this.regional(water,state,zip,table);
          window.SSUI.open(this.dialog,button);
        }

      } catch (_) { status.textContent = this.copy.zip_unavailable; }
      finally { button.disabled = false; form.removeAttribute('aria-busy'); }
    }
  }
  customElements.define('ss-proof-records', ProofRecords);
})();
